import * as React from 'react';
import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Outlet } from 'react-router';

import { withIcon } from '../../../core/components/Icon.tsx';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';

import { url } from '../../../core/utils/link.ts';
import classnames from '../../../core/utils/classnames.tsx';

import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page';
import Table from '../../../core/components/Table.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';

import { GET_SHAREPOINT_INTEGRATIONS } from '../../queries.ts';
import {
    CUSTOMER_INTEGRATION_ERP_PATH,
    CUSTOMER_INTEGRATION_SHAREPOINT_PATH,
    CUSTOMER_INTEGRATION_SHAREPOINT_CONNECT_PATH,
} from '../../constants.ts';

import { useToaster } from '../../../core/components/Toast.tsx';
import SharePointRow from './SharePointRow.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';

const AddIcon = withIcon(faPlus);

const SharePointIntegrationPage = () => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { data, loading, refetch } = useQuery(GET_SHAREPOINT_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.sharepointIntegrations || [];

    useEffect(() => {
        const status = searchParams.get('sharepointIntegration');

        if (status === 'success') {
            publishToast({ description: t('sharepoint.connectSuccess'), status: 'success' });
        } else if (status === 'error') {
            publishToast({ description: t('sharepoint.connectError'), status: 'error' });
        }

        if (status) {
            navigate(CUSTOMER_INTEGRATION_SHAREPOINT_PATH, { replace: true });
        }
    }, []);

    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')}`;

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('sharepoint.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_SHAREPOINT_CONNECT_PATH)}>
                                <AddIcon />
                                {t('sharepoint.connectButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-48 w-48">{t('sharepoint.table.name')}</Table.HeadCell>
                                <Table.HeadCell>{t('sharepoint.table.status')}</Table.HeadCell>
                                <Table.HeadCell>{t('sharepoint.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <SharePointRow key={integration.id} integration={integration} refetch={refetch} />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={4} className="text-center text-content-medium">
                                        {t('sharepoint.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, refetch }} />
        </Layout>
    );
};

export default SharePointIntegrationPage;
